import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as roleApi from '../../api/role';
import { fetchUserPermissions } from './userSlice';

// 获取角色列表
export const fetchRoleList = createAsyncThunk(
  'role/fetchRoleList',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await roleApi.getRoleList(params);
      if (response && response.code === 200) {
        // 返回数据和查询参数，以便在reducer中保存
        return {
          data: response.data,
          params: params
        };
      }
      return rejectWithValue(response?.message || '获取角色列表失败');
    } catch (error) {
      return rejectWithValue(error.message || '获取角色列表失败');
    }
  }
);

// 获取角色详情
export const fetchRoleDetail = createAsyncThunk(
  'role/fetchRoleDetail',
  async (id, { rejectWithValue }) => {
    try {
      const response = await roleApi.getRoleDetail(id);
      if (response && response.code === 200) {
        return response.data;
      }
      return rejectWithValue(response?.message || '获取角色详情失败');
    } catch (error) {
      return rejectWithValue(error.message || '获取角色详情失败');
    }
  }
);

// 创建角色
export const createRole = createAsyncThunk(
  'role/createRole',
  async (data, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await roleApi.createRole(data);
      if (response && response.code === 200) {
        // 创建成功后重新获取角色列表
        const { role } = getState();
        const { roleList } = role;
        const params = {
          pageNum: roleList.pageNum || 1,
          pageSize: roleList.pageSize || 10,
          // 保留搜索参数
          ...roleList.searchParams
        };
        dispatch(fetchRoleList(params));
        return response.data;
      }
      return rejectWithValue(response?.message || '创建角色失败');
    } catch (error) {
      return rejectWithValue(error.message || '创建角色失败');
    }
  }
);

// 更新角色
export const updateRole = createAsyncThunk(
  'role/updateRole',
  async ({ id, data }, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await roleApi.updateRole(id, data);
      if (response && response.code === 200) {
        // 更新成功后重新获取角色列表
        const { role } = getState();
        const { roleList } = role;
        const params = {
          pageNum: roleList.pageNum || 1,
          pageSize: roleList.pageSize || 10,
          // 保留搜索参数
          ...roleList.searchParams
        };
        dispatch(fetchRoleList(params));
        return response.data;
      }
      return rejectWithValue(response?.message || '更新角色失败');
    } catch (error) {
      return rejectWithValue(error.message || '更新角色失败');
    }
  }
);

// 删除角色
export const deleteRole = createAsyncThunk(
  'role/deleteRole',
  async (id, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await roleApi.deleteRole(id);
      if (response && response.code === 200) {
        // 删除成功后重新获取角色列表
        const { role } = getState();
        const { roleList } = role;
        const params = {
          pageNum: roleList.pageNum || 1,
          pageSize: roleList.pageSize || 10,
          // 保留搜索参数
          ...roleList.searchParams
        };
        dispatch(fetchRoleList(params));
        return id;
      }
      return rejectWithValue(response?.message || '删除角色失败');
    } catch (error) {
      return rejectWithValue(error.message || '删除角色失败');
    }
  }
);

// 修改角色状态
export const updateRoleStatus = createAsyncThunk(
  'role/updateRoleStatus',
  async ({ id, status }, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await roleApi.updateRoleStatus(id, status);
      if (response && response.code === 200) {
        // 状态修改成功后重新获取角色列表
        const { role } = getState();
        const { roleList } = role;
        const params = {
          pageNum: roleList.pageNum || 1,
          pageSize: roleList.pageSize || 10,
          // 保留搜索参数
          ...roleList.searchParams
        };
        dispatch(fetchRoleList(params));
        return response.data;
      }
      return rejectWithValue(response?.message || '修改角色状态失败');
    } catch (error) {
      return rejectWithValue(error.message || '修改角色状态失败');
    }
  }
);

// 获取角色的权限
export const fetchRolePermissions = createAsyncThunk(
  'role/fetchRolePermissions',
  async (roleId, { rejectWithValue, getState }) => {
    try {
      const response = await roleApi.getRolePermissions(roleId);
      if (response && response.code === 200) {
        // 获取当前角色信息
        const { role } = getState();
        const currentRole = role.roleList.data.find(r => r.id === roleId);
        
        return {
          roleId,
          permissions: response.data || [],
          currentRole
        };
      }
      return rejectWithValue(response?.message || '获取角色权限失败');
    } catch (error) {
      return rejectWithValue(error.message || '获取角色权限失败');
    }
  }
);

// 分配角色权限
export const assignRolePermissions = createAsyncThunk(
  'role/assignRolePermissions',
  async ({ roleId, permissionIds }, { rejectWithValue, dispatch }) => {
    try {
      const response = await roleApi.assignRolePermissions(roleId, permissionIds);
      if (response && response.code === 200) {
        // 分配角色权限成功后，刷新用户权限
        dispatch(fetchUserPermissions(true));
        return {
          roleId,
          permissionIds
        };
      }
      return rejectWithValue(response?.message || '分配角色权限失败');
    } catch (error) {
      return rejectWithValue(error.message || '分配角色权限失败');
    }
  }
);

// 初始状态
const initialState = {
  roleList: {
    data: [],
    total: 0,
    pageNum: 1,
    pageSize: 10,
    loading: false,
    error: null,
    searchParams: {}
  },
  roleDetail: {
    data: null,
    loading: false,
    error: null
  },
  rolePermissions: {
    currentRoleId: null,
    currentRole: null,
    permissions: [],
    loading: false,
    error: null
  },
  createUpdateStatus: {
    loading: false,
    success: false,
    error: null,
  },
  deleteStatus: {
    loading: false,
    success: false,
    error: null,
  },
  assignPermStatus: {
    loading: false,
    success: false,
    error: null,
  }
};

// 创建slice
const roleSlice = createSlice({
  name: 'role',
  initialState,
  reducers: {
    clearCreateUpdateStatus: (state) => {
      state.createUpdateStatus = {
        loading: false,
        success: false,
        error: null,
      };
    },
    clearDeleteStatus: (state) => {
      state.deleteStatus = {
        loading: false,
        success: false,
        error: null,
      };
    },
    clearAssignPermStatus: (state) => {
      state.assignPermStatus = {
        loading: false,
        success: false,
        error: null,
      };
    }
  },
  extraReducers: (builder) => {
    builder
      // 处理获取角色列表
      .addCase(fetchRoleList.pending, (state) => {
        state.roleList.loading = true;
        state.roleList.error = null;
      })
      .addCase(fetchRoleList.fulfilled, (state, action) => {
        state.roleList.loading = false;
      
        state.roleList.data = action.payload.data.records || [];
        state.roleList.total = action.payload.data.total || 0;
        // 保存当前分页和搜索参数信息
        if (action.payload.params) {
          state.roleList.pageNum = action.payload.params.pageNum || 1;
          state.roleList.pageSize = action.payload.params.pageSize || 10;
          state.roleList.searchParams = action.payload.params;
        }
      })
      .addCase(fetchRoleList.rejected, (state, action) => {
        state.roleList.loading = false;
        state.roleList.error = action.payload;
      })

      // 处理获取角色详情
      .addCase(fetchRoleDetail.pending, (state) => {
        state.roleDetail.loading = true;
        state.roleDetail.error = null;
      })
      .addCase(fetchRoleDetail.fulfilled, (state, action) => {
        state.roleDetail.loading = false;
        state.roleDetail.data = action.payload;
      })
      .addCase(fetchRoleDetail.rejected, (state, action) => {
        state.roleDetail.loading = false;
        state.roleDetail.error = action.payload;
      })

      // 处理创建角色
      .addCase(createRole.pending, (state) => {
        state.createUpdateStatus.loading = true;
        state.createUpdateStatus.success = false;
        state.createUpdateStatus.error = null;
      })
      .addCase(createRole.fulfilled, (state) => {
        state.createUpdateStatus.loading = false;
        state.createUpdateStatus.success = true;
      })
      .addCase(createRole.rejected, (state, action) => {
        state.createUpdateStatus.loading = false;
        state.createUpdateStatus.error = action.payload;
      })

      // 处理更新角色
      .addCase(updateRole.pending, (state) => {
        state.createUpdateStatus.loading = true;
        state.createUpdateStatus.success = false;
        state.createUpdateStatus.error = null;
      })
      .addCase(updateRole.fulfilled, (state) => {
        state.createUpdateStatus.loading = false;
        state.createUpdateStatus.success = true;
      })
      .addCase(updateRole.rejected, (state, action) => {
        state.createUpdateStatus.loading = false;
        state.createUpdateStatus.error = action.payload;
      })

      // 处理删除角色
      .addCase(deleteRole.pending, (state) => {
        state.deleteStatus.loading = true;
        state.deleteStatus.success = false;
        state.deleteStatus.error = null;
      })
      .addCase(deleteRole.fulfilled, (state) => {
        state.deleteStatus.loading = false;
        state.deleteStatus.success = true;
      })
      .addCase(deleteRole.rejected, (state, action) => {
        state.deleteStatus.loading = false;
        state.deleteStatus.error = action.payload;
      })

      // 处理修改角色状态
      .addCase(updateRoleStatus.pending, (state) => {
        state.createUpdateStatus.loading = true;
        state.createUpdateStatus.success = false;
        state.createUpdateStatus.error = null;
      })
      .addCase(updateRoleStatus.fulfilled, (state) => {
        state.createUpdateStatus.loading = false;
        state.createUpdateStatus.success = true;
      })
      .addCase(updateRoleStatus.rejected, (state, action) => {
        state.createUpdateStatus.loading = false;
        state.createUpdateStatus.error = action.payload;
      })

      // 处理获取角色权限
      .addCase(fetchRolePermissions.pending, (state) => {
        state.rolePermissions.loading = true;
        state.rolePermissions.error = null;
      })
      .addCase(fetchRolePermissions.fulfilled, (state, action) => {
        state.rolePermissions.loading = false;
        state.rolePermissions.currentRoleId = action.payload.roleId;
        state.rolePermissions.currentRole = action.payload.currentRole;
        state.rolePermissions.permissions = action.payload.permissions || [];
      })
      .addCase(fetchRolePermissions.rejected, (state, action) => {
        state.rolePermissions.loading = false;
        state.rolePermissions.error = action.payload;
      })

      // 处理分配角色权限
      .addCase(assignRolePermissions.pending, (state) => {
        state.assignPermStatus.loading = true;
        state.assignPermStatus.success = false;
        state.assignPermStatus.error = null;
      })
      .addCase(assignRolePermissions.fulfilled, (state) => {
        state.assignPermStatus.loading = false;
        state.assignPermStatus.success = true;
      })
      .addCase(assignRolePermissions.rejected, (state, action) => {
        state.assignPermStatus.loading = false;
        state.assignPermStatus.error = action.payload;
      });
  },
});

export const { clearCreateUpdateStatus, clearDeleteStatus, clearAssignPermStatus } = roleSlice.actions;

export default roleSlice.reducer; 